import { SKETCH_SNAP_LABELS, formatMm } from '@bw/core';
import type { Sketch, SketchPoint, SketchSegment, SketchShape, SketchSnap } from '@bw/core';
import { shapeSegments } from '@bw/core';
import type { Viewport } from './viewport.js';
import type { DrawTheme } from './theme.js';

/** How the sketch tool interprets a drag: a free chain, or a rectangle. */
export type SketchDrawMode = 'polyline' | 'rectangle';

/** What the sketch tool is currently drawing, if anything. */
export interface SketchDraft {
  readonly points: readonly SketchPoint[];
  readonly cursor: SketchPoint | null;
  readonly kind: 'wall' | 'construction';
  /** True when the cursor is over the first point, so a click would close it. */
  readonly closing: boolean;
}

/** One closed shape, or one loose line. */
export interface SketchRef {
  readonly kind: 'shape' | 'segment';
  readonly id: string;
}

export interface SketchOverlay {
  readonly sketch: Sketch;
  readonly draft?: SketchDraft | undefined;
  /** What is selected in the drawing. */
  readonly selection?: SketchRef | null | undefined;
  /** What a press would take hold of. */
  readonly hover?:
    | { readonly kind: 'vertex'; readonly point: SketchPoint }
    | {
        readonly kind: 'line';
        readonly target: SketchRef;
        readonly line: { readonly a: SketchPoint; readonly b: SketchPoint };
      }
    | null
    | undefined;
  /** Where the point being placed has caught, and on what. */
  readonly snap?: SketchSnap | null | undefined;
  /** A corner being dragged, and where it has got to. */
  readonly dragging?:
    | { readonly shapeId: string | null; readonly index: number; readonly point: SketchPoint }
    | null
    | undefined;
  /** A whole shape or line being dragged, and how far it has come. */
  readonly moving?:
    | { readonly target: SketchRef; readonly dx: number; readonly dy: number }
    | null
    | undefined;
  readonly hoverPoint?: SketchPoint | null | undefined;
}

const FILLS: Readonly<Record<SketchShape['role'], keyof DrawTheme>> = {
  wall: 'shapeWall',
  window: 'shapeWindow',
  door: 'shapeDoor',
  flint: 'shapeFlint',
};

const fillFor = (theme: DrawTheme, shape: SketchShape): string => theme[FILLS[shape.role]];

function strokeFor(theme: DrawTheme, kind: 'wall' | 'construction'): string {
  return kind === 'construction' ? theme.sketchConstruction : theme.sketchWall;
}

function applyLineStyle(
  ctx: CanvasRenderingContext2D,
  theme: DrawTheme,
  kind: 'wall' | 'construction',
): void {
  ctx.strokeStyle = strokeFor(theme, kind);
  // A wall line is part of the wall and reads that way; a construction line is
  // a guide, so it stays quiet without becoming invisible.
  ctx.lineWidth = kind === 'construction' ? 1 : 2;
  ctx.setLineDash(kind === 'construction' ? [6, 5] : []);
  ctx.globalAlpha = kind === 'construction' ? 0.75 : 1;
}

function path(ctx: CanvasRenderingContext2D, viewport: Viewport, segment: SketchSegment): void {
  ctx.beginPath();
  ctx.moveTo(viewport.toScreenX(segment.a.x), viewport.toScreenY(segment.a.y));
  ctx.lineTo(viewport.toScreenX(segment.b.x), viewport.toScreenY(segment.b.y));
  ctx.stroke();
}

function vertex(ctx: CanvasRenderingContext2D, x: number, y: number, colour: string, radius = 3.5): void {
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.fillStyle = colour;
  ctx.fill();
  ctx.strokeStyle = '#FFFFFF';
  ctx.lineWidth = 1.5;
  ctx.setLineDash([]);
  ctx.stroke();
}

const isSame = (a: SketchRef | null | undefined, kind: SketchRef['kind'], id: string): boolean =>
  a != null && a.kind === kind && a.id === id;

/**
 * The marker that says where a point has caught, and on what.
 *
 * A glyph and a word, both: the glyph is read at a glance once the shapes are
 * learned, and the word is read the first time. Without either, a point that
 * has locked onto a brick corner and a point that merely passes near one look
 * exactly alike, which makes the snapping worse than useless — it moves the
 * drawing somewhere the drawer did not ask for and does not know about.
 */
function paintSnap(
  ctx: CanvasRenderingContext2D,
  viewport: Viewport,
  theme: DrawTheme,
  snap: SketchSnap,
): void {
  const x = viewport.toScreenX(snap.x);
  const y = viewport.toScreenY(snap.y);
  ctx.save();
  ctx.globalAlpha = 1;
  ctx.setLineDash([]);
  ctx.strokeStyle = theme.sketchSnap;
  ctx.fillStyle = theme.sketchSnap;
  ctx.lineWidth = 2;

  // The line it caught, picked out along its whole length, so it is obvious
  // which of several lines under the pointer was the one.
  if (snap.line !== undefined) {
    ctx.globalAlpha = 0.65;
    ctx.beginPath();
    ctx.moveTo(viewport.toScreenX(snap.line.a.x), viewport.toScreenY(snap.line.a.y));
    ctx.lineTo(viewport.toScreenX(snap.line.b.x), viewport.toScreenY(snap.line.b.y));
    ctx.stroke();
    ctx.globalAlpha = 1;
  }

  const r = 6;
  ctx.beginPath();
  switch (snap.kind) {
    case 'point':
    case 'brick-corner':
      // A square: one exact place.
      ctx.rect(x - r, y - r, r * 2, r * 2);
      break;
    case 'midpoint':
      // A triangle, the surveyor's mark for a midpoint.
      ctx.moveTo(x, y - r);
      ctx.lineTo(x + r, y + r);
      ctx.lineTo(x - r, y + r);
      ctx.closePath();
      break;
    case 'line':
    case 'brick-edge':
      // A diamond: somewhere along something, rather than a place of its own.
      ctx.moveTo(x, y - r);
      ctx.lineTo(x + r, y);
      ctx.lineTo(x, y + r);
      ctx.lineTo(x - r, y);
      ctx.closePath();
      break;
    default: {
      // Level and plumb: the guide runs off the marker in the direction held.
      ctx.moveTo(x - r, y);
      ctx.lineTo(x + r, y);
      ctx.moveTo(x, y - r);
      ctx.lineTo(x, y + r);
      break;
    }
  }
  ctx.stroke();

  ctx.font = 'bold 11px system-ui, sans-serif';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  const label = SKETCH_SNAP_LABELS[snap.kind];
  const width = ctx.measureText(label).width;
  ctx.globalAlpha = 0.92;
  ctx.fillStyle = theme.measureLabelBg;
  ctx.fillRect(x + r + 4, y - r - 15, width + 10, 16);
  ctx.globalAlpha = 1;
  ctx.fillStyle = theme.measureLabelFg;
  ctx.fillText(label, x + r + 9, y - r - 3);
  ctx.restore();
}

/** A shape or line under the pointer, or being dragged, picked out. */
function outline(
  ctx: CanvasRenderingContext2D,
  viewport: Viewport,
  points: readonly SketchPoint[],
  closed: boolean,
  colour: string,
  width: number,
  dash: readonly number[] = [],
): void {
  if (points.length === 0) return;
  ctx.save();
  ctx.globalAlpha = 1;
  ctx.beginPath();
  points.forEach((p, index) => {
    const sx = viewport.toScreenX(p.x);
    const sy = viewport.toScreenY(p.y);
    if (index === 0) ctx.moveTo(sx, sy);
    else ctx.lineTo(sx, sy);
  });
  if (closed) ctx.closePath();
  ctx.strokeStyle = colour;
  ctx.lineWidth = width;
  ctx.setLineDash([...dash]);
  ctx.stroke();
  ctx.restore();
}

/** Paint the drawn outline: shapes first, then loose lines, then the draft. */
export function paintSketch(
  ctx: CanvasRenderingContext2D,
  viewport: Viewport,
  theme: DrawTheme,
  overlay: SketchOverlay,
): void {
  const { sketch, draft } = overlay;
  ctx.save();

  // A halo under the line the pointer is on, before anything is drawn over it.
  // The outline of the shape it belongs to is picked out as well — the halo
  // says what would be grabbed, and the outline says what would then move —
  // but the halo is what carries it: a line one shade off its neighbours is
  // not an affordance, and drawn geometry has no other way of saying it can be
  // taken hold of at all.
  if (overlay.hover?.kind === 'line') {
    ctx.save();
    ctx.globalAlpha = 0.3;
    ctx.strokeStyle = theme.sketchHover;
    ctx.lineWidth = 9;
    ctx.lineCap = 'round';
    ctx.setLineDash([]);
    ctx.beginPath();
    ctx.moveTo(viewport.toScreenX(overlay.hover.line.a.x), viewport.toScreenY(overlay.hover.line.a.y));
    ctx.lineTo(viewport.toScreenX(overlay.hover.line.b.x), viewport.toScreenY(overlay.hover.line.b.y));
    ctx.stroke();
    ctx.restore();
  }

  for (const shape of sketch.shapes) {
    ctx.globalAlpha = 1;
    ctx.beginPath();
    shape.points.forEach((p, index) => {
      const sx = viewport.toScreenX(p.x);
      const sy = viewport.toScreenY(p.y);
      if (index === 0) ctx.moveTo(sx, sy);
      else ctx.lineTo(sx, sy);
    });
    ctx.closePath();
    ctx.fillStyle = fillFor(theme, shape);
    ctx.fill();
    applyLineStyle(ctx, theme, shape.kind);
    if (isSame(overlay.selection, 'shape', shape.id)) {
      ctx.lineWidth += 2;
      ctx.strokeStyle = theme.selectionOutline;
    } else if (overlay.hover?.kind === 'line' && isSame(overlay.hover.target, 'shape', shape.id)) {
      ctx.lineWidth += 1;
      ctx.strokeStyle = theme.sketchHover;
    }
    ctx.stroke();

    // Label the role at the shape's centre, where there is room for it.
    if (shape.role !== 'wall') {
      let cx = 0;
      let cy = 0;
      for (const p of shape.points) {
        cx += p.x;
        cy += p.y;
      }
      cx /= shape.points.length;
      cy /= shape.points.length;
      ctx.globalAlpha = 1;
      ctx.setLineDash([]);
      ctx.font = 'bold 11px system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillStyle = strokeFor(theme, shape.kind);
      ctx.fillText(shape.role.toUpperCase(), viewport.toScreenX(cx), viewport.toScreenY(cy));
    }
  }

  for (const segment of sketch.segments) {
    applyLineStyle(ctx, theme, segment.kind);
    if (isSame(overlay.selection, 'segment', segment.id)) {
      ctx.lineWidth += 2;
      ctx.strokeStyle = theme.selectionOutline;
    } else if (overlay.hover?.kind === 'line' && isSame(overlay.hover.target, 'segment', segment.id)) {
      ctx.lineWidth += 1;
      ctx.strokeStyle = theme.sketchHover;
    }
    path(ctx, viewport, segment);
  }

  // Vertices, so the geometry can be seen and re-snapped to.
  ctx.globalAlpha = 1;
  for (const shape of sketch.shapes) {
    for (const p of shape.points) {
      vertex(ctx, viewport.toScreenX(p.x), viewport.toScreenY(p.y), strokeFor(theme, shape.kind), 2.5);
    }
  }
  for (const segment of sketch.segments) {
    for (const p of [segment.a, segment.b]) {
      vertex(ctx, viewport.toScreenX(p.x), viewport.toScreenY(p.y), strokeFor(theme, segment.kind), 2.5);
    }
  }

  // A corner being dragged: the two edges it holds follow it, so the shape
  // that would result is visible before the button is let go.
  const dragging = overlay.dragging;
  if (dragging != null) {
    const ring =
      dragging.shapeId === null
        ? null
        : sketch.shapes.find((shape) => shape.id === dragging.shapeId)?.points ?? null;
    if (ring !== null && ring.length > 1) {
      const before = ring[(dragging.index - 1 + ring.length) % ring.length] as SketchPoint;
      const after = ring[(dragging.index + 1) % ring.length] as SketchPoint;
      outline(ctx, viewport, [before, dragging.point, after], false, theme.selectionOutline, 2, [5, 4]);
    } else if (dragging.shapeId === null) {
      const segment = sketch.segments[Math.floor(dragging.index / 2)];
      if (segment !== undefined) {
        const other = dragging.index % 2 === 0 ? segment.b : segment.a;
        outline(ctx, viewport, [other, dragging.point], false, theme.selectionOutline, 2, [5, 4]);
      }
    }
    vertex(ctx, viewport.toScreenX(dragging.point.x), viewport.toScreenY(dragging.point.y), theme.selectionOutline, 4);
  }

  // A whole shape or line being dragged: a ghost where it would land, with the
  // original left in place until the button is let go.
  const moving = overlay.moving;
  if (moving != null && (moving.dx !== 0 || moving.dy !== 0)) {
    const shift = (p: SketchPoint): SketchPoint => ({ x: p.x + moving.dx, y: p.y + moving.dy });
    if (moving.target.kind === 'shape') {
      const shape = sketch.shapes.find((candidate) => candidate.id === moving.target.id);
      if (shape !== undefined) {
        outline(ctx, viewport, shape.points.map(shift), true, theme.selectionOutline, 2, [6, 4]);
      }
    } else {
      const segment = sketch.segments.find((candidate) => candidate.id === moving.target.id);
      if (segment !== undefined) {
        outline(ctx, viewport, [shift(segment.a), shift(segment.b)], false, theme.selectionOutline, 2, [6, 4]);
      }
    }
  }

  if (draft !== undefined && draft.points.length > 0) {
    applyLineStyle(ctx, theme, draft.kind);
    ctx.beginPath();
    draft.points.forEach((p, index) => {
      const sx = viewport.toScreenX(p.x);
      const sy = viewport.toScreenY(p.y);
      if (index === 0) ctx.moveTo(sx, sy);
      else ctx.lineTo(sx, sy);
    });
    if (draft.cursor !== null) {
      ctx.lineTo(viewport.toScreenX(draft.cursor.x), viewport.toScreenY(draft.cursor.y));
    }
    ctx.stroke();

    const first = draft.points[0] as SketchPoint;
    ctx.globalAlpha = 1;
    for (const p of draft.points) {
      vertex(ctx, viewport.toScreenX(p.x), viewport.toScreenY(p.y), theme.sketchVertex, 3);
    }
    // The first point grows when a click there would close the shape.
    vertex(
      ctx,
      viewport.toScreenX(first.x),
      viewport.toScreenY(first.y),
      draft.closing ? theme.previewValid : theme.sketchVertex,
      draft.closing ? 7 : 4,
    );

    // Live length of the segment being drawn.
    if (draft.cursor !== null) {
      const last = draft.points[draft.points.length - 1] as SketchPoint;
      const length = Math.hypot(draft.cursor.x - last.x, draft.cursor.y - last.y);
      if (length > 0) {
        ctx.setLineDash([]);
        ctx.font = 'bold 11px system-ui, sans-serif';
        ctx.textAlign = 'left';
        ctx.fillStyle = strokeFor(theme, draft.kind);
        ctx.fillText(
          `${formatMm(length)} mm`,
          viewport.toScreenX(draft.cursor.x) + 10,
          viewport.toScreenY(draft.cursor.y) - 8,
        );
      }
    }
  }

  if (overlay.hoverPoint != null) {
    vertex(
      ctx,
      viewport.toScreenX(overlay.hoverPoint.x),
      viewport.toScreenY(overlay.hoverPoint.y),
      theme.measureHover,
      5,
    );
  }

  // A vertex under the pointer, grown so it can be seen to be grabbable.
  if (overlay.hover?.kind === 'vertex') {
    vertex(
      ctx,
      viewport.toScreenX(overlay.hover.point.x),
      viewport.toScreenY(overlay.hover.point.y),
      theme.sketchHover,
      5,
    );
  }

  // Last of all, over everything, because it is the one thing that has to be
  // read before the mouse button is let go.
  if (overlay.snap != null) paintSnap(ctx, viewport, theme, overlay.snap);
  ctx.restore();
}

export { shapeSegments };
